import React, {useState} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@material-ui/core";
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  GetApp as DownloadIcon,
} from "@material-ui/icons";
import {toast} from "react-toastify";
import axios from "axios";

export default function ImportParticipantsDialog({open, onClose, workshopId}) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const API_URL = window?.location?.hostname?.includes("maroc")
    ? process.env.REACT_APP_API_MAROC
    : process.env.REACT_APP_API;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (selectedFile) => {
    const validTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ];

    if (!validTypes.includes(selectedFile.type)) {
      toast.error("Please upload a valid Excel or CSV file");
      return;
    }

    setFile(selectedFile);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const submit = async () => {
    if (!file) {
      toast.warning("Please select a file to upload");
      return;
    }

    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);

    try {
      await axios.post(`${API_URL}/workshops/${workshopId}/participants/import`, fd);
      toast.success("Participants imported successfully");
      setFile(null);
      onClose();
    } catch (err) {
      toast.error("Failed to import participants. Please check your file format.");
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Create a simple CSV template
    const template =
      "Prefix,First Name,Last Name,School,Email,Phone\nMr.,John,Doe,ABC University,john@example.com,+1234567890\nMs.,Jane,Smith,XYZ College,jane@example.com,+0987654321";
    const blob = new Blob([template], {type: "text/csv"});
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "participants_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" style={{fontWeight: 600}}>
          Import Participants from Excel
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Instructions */}
        <Paper
          elevation={0}
          style={{
            padding: 16,
            backgroundColor: "#f0f9ff",
            marginBottom: 16,
          }}
        >
          <Typography variant="subtitle2" gutterBottom style={{fontWeight: 600}}>
            File Format Requirements
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon style={{minWidth: 32}}>
                <InfoIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Excel (.xlsx, .xls) or CSV file"
                primaryTypographyProps={{variant: "caption"}}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon style={{minWidth: 32}}>
                <InfoIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="First row should contain headers"
                primaryTypographyProps={{variant: "caption"}}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon style={{minWidth: 32}}>
                <InfoIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Columns: Prefix, First Name, Last Name, School, Email, Phone"
                primaryTypographyProps={{variant: "caption"}}
              />
            </ListItem>
          </List>
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={downloadTemplate}
            style={{textTransform: "none", marginTop: 8}}
          >
            Download Template
          </Button>
        </Paper>

        {/* Upload Area */}
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          style={{
            border: `2px dashed ${dragActive ? "#f59e0b" : "#cbd5e0"}`,
            borderRadius: 12,
            padding: 32,
            textAlign: "center",
            backgroundColor: dragActive ? "#fef3c7" : "#f9fafb",
            transition: "all 0.3s ease",
            cursor: "pointer",
          }}
          onClick={() => document.getElementById("file-input").click()}
        >
          <input
            id="file-input"
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileChange}
            style={{display: "none"}}
          />

          {file ? (
            <Box>
              <CheckIcon style={{fontSize: 48, color: "#10b981", marginBottom: 8}} />
              <Typography variant="subtitle1" gutterBottom style={{fontWeight: 600}}>
                {file?.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {(file?.size / 1024).toFixed(2)} KB
              </Typography>
              <Box mt={2}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  style={{textTransform: "none"}}
                >
                  Choose Different File
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <UploadIcon style={{fontSize: 48, color: "#9ca3af", marginBottom: 8}} />
              <Typography variant="subtitle1" gutterBottom style={{fontWeight: 600}}>
                Drop your file here or click to browse
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Supports Excel (.xlsx, .xls) and CSV files
              </Typography>
            </Box>
          )}
        </Box>

        {uploading && (
          <Box mt={2}>
            <LinearProgress color="secondary" />
            <Typography
              variant="caption"
              color="textSecondary"
              align="center"
              display="block"
              style={{marginTop: 8}}
            >
              Uploading and processing participants...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions style={{padding: 16}}>
        <Button onClick={handleClose} disabled={uploading} style={{textTransform: "none"}}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={submit}
          disabled={!file || uploading}
          startIcon={<UploadIcon />}
          style={{textTransform: "none", fontWeight: 600}}
        >
          {uploading ? "Importing..." : "Import Participants"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
